import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Line } from 'react-chartjs-2';
import { ErrorBoundary } from 'react-error-boundary';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';

// Add error fallback component
const ErrorFallback = ({ error, resetErrorBoundary }) => {
    return (
        <div className="p-4 bg-red-500/10 rounded-lg">
            <h2 className="text-xl font-bold mb-2">Something went wrong:</h2>
            <pre className="text-sm text-red-200">{error.message}</pre>
            <button 
                onClick={resetErrorBoundary}
                className="mt-4 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
            >
                Try again
            </button>
        </div>
    );
};

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

const regionColors = {
    'SE1': '#59a14f', // Green
    'SE2': '#4e79a7', // Blue
    'SE3': '#edc949', // Yellow
    'SE4': '#e15759'  // Red
};

const generateChartData = (trendsData) => {
    console.log('Raw trends data:', trendsData);
    
    if (!trendsData || Object.keys(trendsData).length === 0) {
        console.warn('No trends data available');
        return null;
    }
    
    // Only process valid SE regions
    const validRegions = ['SE1', 'SE2', 'SE3', 'SE4'];
    const labels = ['Idag', 'Igår', 'Förra veckan', 'Förra månaden', 'Förra året'];
    
    const datasets = Object.entries(trendsData)
        .filter(([region]) => validRegions.includes(region))
        .map(([region, data]) => {
            if (!data || typeof data !== 'object') {
                console.error(`Invalid data for region ${region}:`, data);
                return null;
            }
            
            return {
                label: region,
                data: [
                    parseFloat(data.todayAvg) || 0,
                    parseFloat(data.yesterdayAvg) || 0,
                    parseFloat(data.lastWeekAvg) || 0,
                    parseFloat(data.lastMonthAvg) || 0,
                    parseFloat(data.lastYearAvg) || 0
                ],
                borderColor: regionColors[region] || '#ffffff',
                tension: 0.4
            };
        })
        .filter(Boolean); // Remove null entries

    console.log('Processed chart data:', { labels, datasets });
    return { labels, datasets };
};

const ComparisonTable = ({ data }) => {
    const validRegions = ['SE1', 'SE2', 'SE3', 'SE4'];
    const filteredData = Object.fromEntries(
        Object.entries(data || {}).filter(([region]) => validRegions.includes(region))
    );
    
    if (Object.keys(filteredData).length === 0) return null;
    
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm text-left text-white">
                <thead>
                    <tr className="border-b border-white/10">
                        <th className="py-3 px-6">Region</th>
                        <th className="py-3 px-6">Idag vs Igår</th>
                        <th className="py-3 px-6">Veckoändring</th>
                        <th className="py-3 px-6">Månadsändrig</th>
                        <th className="py-3 px-6">Årsändring</th>
                    </tr>
                </thead>
                <tbody>
                    {Object.entries(filteredData).map(([region, values]) => (
                        <tr key={region} className="border-b border-white/10">
                            <td className="py-3 px-6">{region}</td>
                            <td className={`py-3 px-6 ${parseFloat(values.changeTodayVsYesterday) < 0 ? 'text-red-400' : 'text-green-400'}`}>
                                {values.changeTodayVsYesterday}%
                            </td>
                            <td className={`py-3 px-6 ${parseFloat(values.changeThisWeekVsLastWeek) < 0 ? 'text-red-400' : 'text-green-400'}`}>
                                {values.changeThisWeekVsLastWeek}%
                            </td>
                            <td className={`py-3 px-6 ${parseFloat(values.changeThisMonthVsLastMonth) < 0 ? 'text-red-400' : 'text-green-400'}`}>
                                {values.changeThisMonthVsLastMonth}%
                            </td>
                            <td className={`py-3 px-6 ${parseFloat(values.changeThisYearVsLastYear) < 0 ? 'text-red-400' : 'text-green-400'}`}>
                                {values.changeThisYearVsLastYear}%
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const TrendCard = ({ title, percentageChange, currentPrice, previousPrice }) => {
    const isPositive = parseFloat(percentageChange) > 0;
    return (
        <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-lg p-4">
            <h3 className="text-lg font-semibold mb-2">{title}</h3>
            <div className={`text-2xl font-bold mb-1 ${isPositive ? 'text-red-400' : 'text-green-400'}`}>
                {percentageChange}%
            </div>
            <div className="text-sm text-white/70">
                {currentPrice} öre/kWh → {previousPrice} öre/kWh
            </div>
        </div>
    );
};

const TrendsComponentContent = () => {
    const [data, setData] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchTrendsData = async () => {
            try {
                setLoading(true);
                const url = `${process.env.REACT_APP_API_URL}/api/trends`;
                console.log('Fetching from:', url);
                
                const response = await axios.get(url);
                console.log('API Response:', response);
                
                if (!response.data || Object.keys(response.data).length === 0) {
                    throw new Error('Empty response received from API');
                }

                // Validate data structure
                const validRegions = ['SE1', 'SE2', 'SE3', 'SE4'];
                const hasValidData = validRegions.some(region => response.data[region]);
                
                if (!hasValidData) {
                    throw new Error('Invalid data structure - missing regions');
                }
                
                setData(response.data);
                setError(null);
            } catch (err) {
                console.error('API Error:', err);
                setError(`Error: ${err.message}. ${err.response?.data?.message || ''}`);
            } finally {
                setLoading(false);
            }
        };

        fetchTrendsData();
    }, []);

    if (error) return <div>{error}</div>;

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            tooltip: {
                enabled: true,
                mode: 'index',
                intersect: false,
                callbacks: {
                    label: function(tooltipItem) {
                        return `${tooltipItem.dataset.label}: ${tooltipItem.raw} öre/kWh`;
                    }
                }
            },
            legend: {
                position: 'top',
                labels: {
                    color: '#ffffff'
                }
            }
        },
        scales: {
            y: {
                title: {
                    display: true,
                    text: 'öre/kWh',
                    color: '#ffffff'
                },
                ticks: {
                    color: '#ffffff'
                },
                grid: {
                    color: 'rgba(255, 255, 255, 0.1)'
                }
            },
            x: {
                title: {
                    display: true,
                    text: 'Tidperiod',
                    color: '#ffffff'
                },
                ticks: {
                    color: '#ffffff'
                },
                grid: {
                    color: 'rgba(255, 255, 255, 0.1)'
                }
            }
        }
    };

    return (
        <div className="min-h-screen overflow-y-auto relative bg-gradient-to-b from-gray-900 to-black text-white">
            <div className="max-w-7xl mx-auto p-4">
                <h2 className="text-2xl sm:text-4xl font-bold mb-3 sm:mb-6">Elpristrender</h2>

                {/* Trend Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                    {!loading && data.SE3 && (
                        <>
                            <TrendCard
                                title="Idag vs. igår"
                                percentageChange={data.SE3.changeTodayVsYesterday}
                                currentPrice={data.SE3.todayAvg}
                                previousPrice={data.SE3.yesterdayAvg}
                            />
                            <TrendCard
                                title="Senaste veckan"
                                percentageChange={data.SE3.changeThisWeekVsLastWeek}
                                currentPrice={data.SE3.todayAvg}
                                previousPrice={data.SE3.lastWeekAvg}
                            />
                            <TrendCard
                                title="Senaste månaden"
                                percentageChange={data.SE3.changeThisMonthVsLastMonth}
                                currentPrice={data.SE3.todayAvg}
                                previousPrice={data.SE3.lastMonthAvg}
                            />
                        </>
                    )}
                    {loading && (
                        <>
                            <Skeleton height={120} />
                            <Skeleton height={120} />
                            <Skeleton height={120} />
                        </>
                    )}
                </div>

                {/* Comparison Table */}
                <div className="mb-8 bg-white/5 backdrop-blur-lg border border-white/10 rounded-lg p-4">
                    <h3 className="text-xl font-bold mb-4">Översikt på prisändringar</h3>
                    {loading ? (
                        <Skeleton count={5} height={40} />
                    ) : (
                        <ComparisonTable data={data} />
                    )}
                </div>

                {/* Price Trends Chart */}
                <div className="chart-container bg-white/5 backdrop-blur-lg border border-white/10 rounded-lg p-4 mb-8 h-[400px]">
                    <h3 className="text-xl font-bold mb-4">Pristrender</h3>
                    {loading ? (
                        <div className="animate-pulse h-full bg-white/5" />
                    ) : (
                        <Line 
                            data={generateChartData(data)} 
                            options={chartOptions}
                            redraw={true}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

// Wrap main component with ErrorBoundary
const TrendsComponent = () => {
    return (
        <ErrorBoundary 
            FallbackComponent={ErrorFallback}
            onReset={() => window.location.reload()}
        >
            <TrendsComponentContent />
        </ErrorBoundary>
    );
};

export default TrendsComponent;
